/* eslint-disable import/prefer-default-export */
import React, { useState } from 'react';
import { translate } from '@utils/i18n';
import Loader, { LoaderSizes } from '@components/v2/Loader';
import CrudHeader from '@components/v2/CrudHeader';
import ExternalEntitySelector from '@components/v2/ExternalEntitySelector';
import Label from '@components/Label';
import Tags from '@components/Tags';
import TabView from '@components/TabView';
import ActivityFeed from '@features/ActivityFeed';
import { namespace } from './constants';
import useProposalDetail from '../hooks/useProposalDetail';
import useAvailableTags from '../hooks/useAvailableTags';
import ActionButtons from './components/ActionButtons';
import ProposalDetailInfo from './components/ProposalDetailInfo';
import useUpdateProposal from '../hooks/useUpdateProposal';
export default function ProposalDetail({ proposalId }) {
    var _a, _b, _c;
    const { data: proposal, isLoading, error, isError } = useProposalDetail(proposalId);
    const { data: availableTags = [] } = useAvailableTags(proposalId);
    const { handleFieldUpdate, updateProposal } = useUpdateProposal(proposalId);
    const [tabSelected, setTabSelected] = useState('details');
    if (isLoading) {
        return React.createElement(Loader, { size: LoaderSizes.large, label: "Loading proposal details" });
    }
    // Show error state if request failed
    if (isError || error) {
        return (React.createElement("div", { className: "proposal-detail-error" },
            React.createElement("div", { className: "error-message" },
                React.createElement("h3", null, translate('error_loading', { namespace })),
                React.createElement("p", null, (error === null || error === void 0 ? void 0 : error.message) || translate('error_loading_message', { namespace })),
                React.createElement("button", { type: "button", className: "retry-button", onClick: () => window.location.reload() }, translate('retry', { namespace })))));
    }
    // Show not found state if no proposal data
    if (!proposal) {
        return (React.createElement("div", { className: "proposal-detail-not-found" },
            React.createElement("div", { className: "warning-message" },
                React.createElement("h3", null, translate('not_found', { namespace })),
                React.createElement("p", null, translate('not_found_message', { namespace })))));
    }
    const onRequestAddNote = () => {
        setTabSelected('activity');
        setTimeout(() => {
            const activityNoteInput = document.getElementById('activity_notes');
            activityNoteInput === null || activityNoteInput === void 0 ? void 0 : activityNoteInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
            activityNoteInput === null || activityNoteInput === void 0 ? void 0 : activityNoteInput.focus();
        }, 500);
    };
    const tabs = [
        {
            id: 'details',
            label: translate('tabs.details', { namespace }),
            content: React.createElement(ProposalDetailInfo, { proposal: proposal, proposalId: proposalId, onRequestAddNote: onRequestAddNote })
        },
        {
            id: 'activity',
            label: translate('tabs.activity', { namespace }),
            content: (React.createElement(ActivityFeed, { buildingId: (_a = proposal.building) === null || _a === void 0 ? void 0 : _a.id, proposalId: proposal.id, notesVisibilityEnabled: false }))
        }
    ];
    return (React.createElement("div", { className: "main-content" },
        React.createElement("div", { className: "page-content" },
            React.createElement(CrudHeader, { title: `${translate('title', { namespace })} #${proposal.proposalNumber}`, subtitle: (_b = proposal.building) === null || _b === void 0 ? void 0 : _b.name },
                React.createElement(ActionButtons, { proposal: proposal })),
            React.createElement("div", { className: "row-fluid d-flex", style: { gap: '1.6rem' } },
                React.createElement("div", { className: "d-flex" },
                    React.createElement(Label, { htmlFor: "statusInput", className: "info-key" },
                        translate('status', { namespace: `${namespace}.fields` }),
                        ":"),
                    React.createElement(ExternalEntitySelector, { id: "statusInput", showSearch: true, maxHeight: 280, allowClear: false, url: "/api/internal/proposal_statuses", value: ((_c = proposal.statusId) === null || _c === void 0 ? void 0 : _c.toString()) || null, onChange: (status) => {
                            var _a;
                            if (!status)
                                return;
                            handleFieldUpdate({ statusId: (_a = status.id) === null || _a === void 0 ? void 0 : _a.toString() });
                        } })),
                React.createElement("div", { className: "form-group" },
                    React.createElement(Tags, { tagsList: proposal.tagList, attributeName: "tag_list", tagsForSelect: availableTags, onTagsChange: (values) => {
                            updateProposal.mutate({ id: proposalId, proposalUpdate: { tagList: values } });
                        } }))),
            React.createElement(TabView, { tabs: tabs, tabSelected: tabSelected, onTabChange: (tab) => setTabSelected(tab) }))));
}
